import React, { useCallback, useEffect, useReducer, useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
// import { router } from 'expo-router';
import { useTheme } from 'tamagui';
// import { MaterialIcons } from '@expo/vector-icons';

import { showErrorMessage } from '../../utils/messageToast';
import { useNavigate } from 'react-router-dom';
import { useResidentRestrictions } from '../useResidentRestrictions';
import { groupsService, COMMENTS_KEYS } from '@cobu/api-client';
import type { Group } from '@cobu/api-client';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { RootState } from '../../store';
import { CommentType, GroupMember } from '@cobu/types';
import { Strings } from '../../constants';
import { logger } from '../../utils/logger';
import groupDetailReducer, {
  setLoading,
  setGroup,
  setGatherings,
  setMembers,
  updateMembership,
  setJoining,
  setLeaving,
  setError,
  clearError,
  resetGroup,
  GroupDetailState,
  GroupGathering,
} from '../../store/groupDetailSlice';

// Query keys
export const GROUP_DETAIL_KEYS = {
  detail: (groupId: string) => ['group', 'detail', groupId],
  gatherings: (groupId: string) => ['group', 'gatherings', groupId],
  members: (groupId: string) => ['group', 'members', groupId],
};

// Initial state for the local reducer
const initialState: GroupDetailState = {
  groups: {},
  isLoading: false,
  error: null,
  isJoining: false,
  isLeaving: false,
};

/**
 * Comprehensive hook for managing all group detail functionality
 */
export const useGroupDetail = (groupId: string, info: boolean = false) => {
  const [state, dispatch] = useReducer(groupDetailReducer, initialState);
  const { user } = useSelector((state: RootState) => state.auth);
  const { groups, isLoading, error, isJoining, isLeaving } = state;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const strings = Strings.get();

  // UI state management
  const theme = useTheme();
  const [operationLoading, setOperationLoading] = useState(false);
  const [operationError, setOperationError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Get current group data from local state
  const groupData = groups[groupId];
  const group = groupData?.group || null;
  const gatherings = groupData?.gatherings || [];
  const members = groupData?.members || [];
  const userIsGroupMember = groupData?.userIsGroupMember || false;

  // Memoize strings to avoid repeated access
  // const strings = useMemo(() => Strings.get(), []);

  // Get building info from Redux
  const buildingName = user?.building?.name;

  // Memoize building-wide group check
  const isBuildingWideGroup = useMemo(() => {
    return group?.isDefault ||
      group?.name === 'Marketplace' ||
      group?.name === buildingName;
  }, [group?.isDefault, group?.name, buildingName]);

  const { canJoinGroup } = useResidentRestrictions();

  // Memoize permission checks to avoid recalculation
  const permissions = useMemo(() => ({
    canViewDiscussion: userIsGroupMember || isBuildingWideGroup,
    canComposeComments: userIsGroupMember || (isBuildingWideGroup && group?.name === 'Marketplace'),
    showDisabledOverlay: !userIsGroupMember && !isBuildingWideGroup && group,
    canJoinGroup,
  }), [userIsGroupMember, isBuildingWideGroup, group?.name, group, canJoinGroup]);

  // Memoize analytics data to prevent recreation on every render
  const analyticsData = useMemo(() => {
    if (!group) return {};
    return {
      groupId: group.uuid,
      groupName: group.name,
      userIsGroupMember,
      isBuildingWideGroup,
    };
  }, [group?.uuid, group?.name, userIsGroupMember, isBuildingWideGroup]);

  // Reset state when groupId changes
  useEffect(() => {
    dispatch(resetGroup(groupId));
  }, [groupId]);

  // Hydrate local reducer from react-query cache to avoid null state on remounts
  useEffect(() => {
    if (!groupId) return;
    const cachedGroup = queryClient.getQueryData<Group>(GROUP_DETAIL_KEYS.detail(groupId + info.toString()));
    if (cachedGroup) {
      dispatch(setGroup({ groupId, group: cachedGroup }));
    }
    const cachedMembers = queryClient.getQueryData<GroupMember[]>(GROUP_DETAIL_KEYS.members(groupId));
    if (cachedMembers) {
      dispatch(setMembers({ groupId, members: cachedMembers }));
    }
    const cachedGatherings = queryClient.getQueryData<GroupGathering[]>(GROUP_DETAIL_KEYS.gatherings(groupId));
    if (cachedGatherings) {
      dispatch(setGatherings({ groupId, gatherings: cachedGatherings }));
    }
  }, [groupId, info, queryClient]);

  // Track group view analytics
  useEffect(() => {
    if (group && analyticsData.groupId) {
      /* ANALYTICS_DISABLED: VIEW_GROUP_DETAILS ('view_group_details')
        analyticsService.trackEvent(AnalyticsEvent.VIEW_GROUP_DETAILS, analyticsData);
        */
    }
  }, [group, analyticsData]);

  // Fetch group data
  const groupQuery = useQuery({
    queryKey: GROUP_DETAIL_KEYS.detail(groupId + info.toString()),
    queryFn: async () => {
      dispatch(setLoading(true));
      try {
        const group = await groupsService.getGroupDetail(groupId);
        dispatch(setGroup({ groupId, group }));
        return group;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch group details';
        dispatch(setError(errorMessage));
        throw error;
      }
    },
    enabled: !!groupId,
    refetchOnMount: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch gatherings data
  const gatheringsQuery = useQuery({
    queryKey: GROUP_DETAIL_KEYS.gatherings(groupId),
    queryFn: async () => {
      try {
        const gatherings = await groupsService.getGroupGatherings(groupId);
        dispatch(setGatherings({ groupId, gatherings }));
        return gatherings;
      } catch (error) {
        logger.error('Failed to fetch gatherings:', error);
        return [];
      }
    },
    enabled: !!groupId && !!group,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch members data
  const membersQuery = useQuery({
    queryKey: GROUP_DETAIL_KEYS.members(groupId),
    queryFn: async () => {
      try {
        const members = await groupsService.getGroupMembers(groupId);
        dispatch(setMembers({ groupId, members }));
        return members;
      } catch (error) {
        logger.error('Failed to fetch members:', error);
        return [];
      }
    },
    enabled: !!groupId && !!group && userIsGroupMember,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Join group mutation
  const joinGroupMutation = useMutation({
    mutationFn: async (groupId: string) => {
      dispatch(setJoining(true));

      /* ANALYTICS_DISABLED: JOIN_GROUP ('join_group')
          analyticsService.trackEvent(AnalyticsEvent.JOIN_GROUP, {
                  user: user,
                  groupId,
                  groupName: group?.name,
                });
          */

      const result = await groupsService.joinGroup({ groupId, role: 0, user: user?.uuid });
      dispatch(setJoining(false));
      return result;
    },
    onSuccess: () => {
      dispatch(updateMembership({ groupId, isMember: true }));

      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ queryKey: ['group', 'detail'], exact: false })
      queryClient.invalidateQueries({ queryKey: GROUP_DETAIL_KEYS.members(groupId) });
      queryClient.invalidateQueries({ queryKey: ['groups'] }); // Refresh groups list
    },
    onError: (error) => {
      dispatch(setJoining(false));
      const errorMessage = error instanceof Error ? error.message : 'Failed to join group';
      dispatch(setError(errorMessage));
    },
  });

  // Leave group mutation
  const leaveGroupMutation = useMutation({
    mutationFn: async (groupId: string) => {
      dispatch(setLeaving(true));

      /* ANALYTICS_DISABLED: LEAVE_GROUP ('leave_group')
          analyticsService.trackEvent(AnalyticsEvent.LEAVE_GROUP, {
                  user: user,
                  groupId,
                  groupName: group?.name,
                });
          */

      // Ensure we have members available
      let groupMembers = members;
      if (!groupMembers || groupMembers.length === 0) {
        try {
          const result = await membersQuery.refetch();
          groupMembers = (result.data as any) || [];
        } catch (e) {
          logger.error('Failed to refetch members before leaving:', e);
          groupMembers = [] as any;
        }
      }

      const groupMembersIndex = groupMembers.findIndex(
        groupMember => groupMember.user.uuid === user?.uuid,
      );

      const result = await groupsService.leaveGroup(groupMembers[groupMembersIndex].uuid);
      dispatch(setLeaving(false));
      return result;
    },
    onSuccess: () => {
      dispatch(updateMembership({ groupId, isMember: false }));

      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ queryKey: ['group', 'detail'], exact: false });
      queryClient.invalidateQueries({ queryKey: GROUP_DETAIL_KEYS.members(groupId) });
      queryClient.invalidateQueries({ queryKey: ['groups'] }); // Refresh groups list
    },
    onError: (error) => {
      dispatch(setLeaving(false));
      const errorMessage = error instanceof Error ? error.message : 'Failed to leave group';
      dispatch(setError(errorMessage));
    },
  });

  // Enhanced join handler with UI state management
  const handleJoinGroup = useCallback(async () => {
    if (!group || isBuildingWideGroup) return;

    setOperationLoading(true);
    setOperationError(null);

    try {
      await joinGroupMutation.mutateAsync(group.uuid);
    } catch (error) {
      logger.error('Failed to join group:', error);

      const errorMessage = error instanceof Error ? error.message : strings.groupDetail.errors.joinFailed;
      setOperationError(errorMessage);

      showErrorMessage(strings.groupDetail.states.errorTitle, errorMessage);
      setOperationError(null);
    } finally {
      setOperationLoading(false);
    }
  }, [group, isBuildingWideGroup, joinGroupMutation, analyticsData, strings]);

  // Enhanced leave handler with UI state management
  const handleLeaveGroup = useCallback(async () => {
    if (!group || isBuildingWideGroup) return;

    setOperationLoading(true);
    setOperationError(null);

    try {
      await leaveGroupMutation.mutateAsync(group.uuid);
    } catch (error) {
      logger.error('Failed to leave group:', error);

      const errorMessage = error instanceof Error ? error.message : strings.groupDetail.errors.leaveFailed;
      setOperationError(errorMessage);

      showErrorMessage(strings.groupDetail.states.errorTitle, errorMessage);
      setOperationError(null);
    } finally {
      setOperationLoading(false);
    }
  }, [group, isBuildingWideGroup, leaveGroupMutation, analyticsData, strings]);

  // Navigation handlers
  const handleInfoPress = useCallback(() => {
    if (!group) return;

    navigate('/groups/info', {
      state: {
        id: group.uuid,
        members,
        group,
        buildingName: buildingName || '',
        currentUser: user,
        currentGroupEvents: gatherings,
        userIsGroupMember,
      },
    });
  }, [group, members, buildingName, user, gatherings, userIsGroupMember, navigate]);

  const handleComposeComment = useCallback((replyTo?: any) => {
    if (!group) return;

    /* ANALYTICS_DISABLED: COMPOSE_COMMENT_STARTED ('compose_comment_started')
      analyticsService.trackEvent(AnalyticsEvent.COMPOSE_COMMENT_STARTED, {
            source: 'group_detail',
            groupId: group.uuid,
            groupName: group.name,
            isReply: !!replyTo,
          });
      */
    navigate({
      pathname: '/chat/compose-comment',
      search: new URLSearchParams({
        groupId: group.uuid,
        enableMarketplaceModerator: 'false',
        ...(replyTo ? { replyingTo: JSON.stringify(replyTo) } : {}),
      }).toString(),
    });
  }, [group, strings]);

  const handleUserProfile = useCallback((userId: string) => {
    /* ANALYTICS_DISABLED: VIEW_USER_PROFILE ('view_user_profile')
      analyticsService.trackEvent(AnalyticsEvent.VIEW_USER_PROFILE, {
            userId,
            source: 'group_detail',
            groupId: group?.uuid,
          });
      */

    navigate(`/public-profile/${userId}`);
  }, [navigate]);

  const handleShowAllMembers = useCallback(() => {
    if (!group) return;

    analyticsService.trackEvent(AnalyticsEvent.VIEW_ALL_MEMBERS, {
      source: 'group_detail',
      groupId: group.uuid,
      groupName: group.name,
    });

    navigate({
      pathname: `/groups/${group.uuid}/members`,
      search: new URLSearchParams({
        groupId: group.uuid,
      }).toString(),
    });
  }, [group]);

  // Header is configured at the screen level using CobuHeader

  // Memoize group discussion props to prevent unnecessary re-renders
  const groupDiscussionProps = useMemo(() => ({
    groupId: group?.uuid || '',
    commentType: CommentType.GroupComment,
    isGroup: true,
    publicChat: isBuildingWideGroup && group?.name === 'Marketplace',
    groupMembers: members as GroupMember[],
    allowReactions: true,
    allowReplies: true,
    allowEditing: true,
    allowReporting: true,
    allowImageUploads: true,
    allowPinning: userIsGroupMember && !isBuildingWideGroup,
    showLoadMoreBtn: true,
    showLoadMoreBtnToLoadMore: true,
    onComposeComment: permissions.canComposeComments ? handleComposeComment : undefined,
    onUserProfile: handleUserProfile,
    onShowAllMembers: handleShowAllMembers,
    maxCommentLength: 2000,
    maxImages: 5,
  }), [
    group?.uuid,
    group?.name,
    isBuildingWideGroup,
    members,
    userIsGroupMember,
    permissions.canComposeComments,
    handleComposeComment,
    handleUserProfile,
    handleShowAllMembers,
  ]);

  // Utility functions
  const clearErrors = useCallback(() => {
    dispatch(clearError());
    setOperationError(null);
  }, [dispatch]);

  const refetchGroup = useCallback(() => {
    groupQuery.refetch();
    gatheringsQuery.refetch();
    if (userIsGroupMember) {
      membersQuery.refetch();
    }
  }, [groupQuery, gatheringsQuery, membersQuery, userIsGroupMember]);

  // Refresh handler for group detail (refreshes both group data and comments)
  const handleGroupDetailRefresh = useCallback(async () => {
    if (!groupId || !group) return;
    
    setIsRefreshing(true);
    try {
      // Refresh group data
      await refetchGroup();
      
      // Refresh comments by refetching queries (waits for completion)
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: COMMENTS_KEYS.list(groupId, undefined, undefined)
        }),
        queryClient.refetchQueries({
          queryKey: COMMENTS_KEYS.pinned(groupId, undefined)
        })
      ]);
    } catch (error) {
      logger.error('Error refreshing group detail:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [groupId, group, refetchGroup, queryClient]);

  // Handle operation error retry
  const handleRetry = useCallback(() => {
    setOperationError(null);
    refetchGroup();
  }, [refetchGroup]);

  const joinGroupHandler = useCallback(
    (groupId: string) => joinGroupMutation.mutateAsync(groupId),
    [joinGroupMutation]
  );

  const leaveGroupHandler = useCallback(
    (groupId: string) => leaveGroupMutation.mutateAsync(groupId),
    [leaveGroupMutation]
  );

  return {
    // Core Data
    group,
    gatherings,
    members,
    userIsGroupMember,
    isBuildingWideGroup,

    // Loading States - only true on initial load, not during refetch
    isLoading: isLoading || (groupQuery.isLoading && !groupQuery.data),
    error: error || groupQuery.error?.message,
    operationLoading,
    operationError,
    isJoining,
    isLeaving,
    isRefreshing,

    // Permissions
    permissions,

    // Configurations
    groupDiscussionProps,

    // Enhanced Handlers
    handleJoinGroup,
    handleLeaveGroup,
    handleComposeComment,
    handleUserProfile,
    handleShowAllMembers,
    handleInfoPress,
    handleRetry,
    handleGroupDetailRefresh,

    // Legacy API (for backward compatibility)
    joinGroup: joinGroupHandler,
    leaveGroup: leaveGroupHandler,
    clearErrors,
    refetchGroup,

    // UI Resources
    // strings,
    // theme,
  };
}; 